import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { StoresContext } from '../../provider/StoreProvider'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { Card } from '../../components/card/Card'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { DetailContent } from '../../components/pagingList'
import { RowData } from './components'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'
import { ReactComponent as ArrowDownRoundIcon } from '../../assets/ico/common/expand/expand_roundDown.svg'
import { ReactComponent as ArrowDownIcon } from '../../assets/ico/common/expand/expand_down.svg'

interface Accordion {
  sale: boolean
  amount: boolean
}

export const SettlementDetail = observer(() => {
  const {
    commonStore: { setInvalidAccess },
    authStore: { userInfo },
    myStore,
  } = useContext(StoresContext)
  const detailStore = myStore.purchaseDetailStore
  const disposalStore = myStore.productDisposalStore

  const [accordion, setAccordion] = useState<Accordion>({ sale: false, amount: false })

  const location = useLocation()

  const userProductId = Number(location.pathname.split('/')[3])

  // 과세대상금액
  const otherIncome =
    typeof disposalStore.content?.otherIncome === 'number' && disposalStore.content.otherIncome >= 50000
      ? disposalStore.content.otherIncome
      : 0

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const response = await detailStore.load({ param: { userProductId } }, true)

      if (
        typeof userInfo === 'undefined' ||
        userInfo?.id !== response?.userId ||
        !response ||
        !['TO_BE_SETTLEMENT', 'SETTLEMENT_FINISH'].includes(response.status)
      ) {
        setInvalidAccess(true)
        return
      }

      await disposalStore.load({ param: { userProductId } }, true)
    } catch (error) {
      setInvalidAccess(true)
    }
  }

  const toggleSaleAccordion = () => setAccordion((prevState) => ({ ...prevState, sale: !prevState.sale }))

  const toggleAmountAccordion = () => setAccordion((prevState) => ({ ...prevState, amount: !prevState.amount }))

  return (
    <Wrap>
      <Header type="SUB" title="매각 대금 정산 내역" />
      <StyledContent backgroundColor="gray_1">
        <DetailContent detailStore={detailStore}>
          <ProductInfoWrap>
            {/* 작품 정보 */}
            <ProductInfoCard>
              <ProductInfoContent $gap={16}>
                <ProductImage>
                  <img src={detailStore.content?.product.imageUri} alt={detailStore.content?.product.name} />
                </ProductImage>
                <ColumnItem $gap={4}>
                  <Text size={18} weight={600}>
                    {detailStore.content?.product.artist}
                  </Text>
                  <Text size={14} color="text_secondary">
                    {detailStore.content?.product.name}
                  </Text>
                </ColumnItem>
                <ProductDetailLink>
                  <Text size={14} weight={500} color="text_secondary">
                    작품 상세 보기
                  </Text>
                  <ArrowRightGrayIcon />
                </ProductDetailLink>
              </ProductInfoContent>
              <DivideLine />
              <AccordionWrap>
                <SaleInfoToggleBox isActive={accordion.sale}>
                  <Card $direction="column" $gap={16} $padding="16px" $radius={8} $background="gray_1">
                    <RowData
                      title="보유 수량"
                      content={{
                        value: `${detailStore.content?.totalShares?.toLocaleString()}주`,
                        size: 18,
                        weight: 600,
                      }}
                    />
                    <RowData
                      title="정산 금액"
                      content={{
                        value: `${disposalStore.content?.amount.toLocaleString()}원`,
                        size: 18,
                        weight: 600,
                      }}
                    />
                  </Card>
                  <ArrowDownRoundIcon onClick={toggleSaleAccordion} />
                </SaleInfoToggleBox>
                <Accordion isActive={accordion.sale}>
                  <ColumnItem $gap={16}>
                    <RowData
                      title="배정 확정일"
                      content={`${dayjs(detailStore.content?.product.activeProductSale?.allotmentDate).format(
                        'YYYY-MM-DD',
                      )}`}
                    />
                    <RowData
                      title="매각 완료일"
                      content={`${dayjs(detailStore.content?.product.sellHistory?.sellDate).format('YYYY-MM-DD')}`}
                    />
                    <RowData
                      title="보유일"
                      content={`${dayjs().diff(
                        detailStore.content?.product.activeProductSale?.allotmentDate,
                        'days',
                      )}일`}
                    />
                    <RowData
                      title="총 발행 금액"
                      content={`${detailStore.content?.product.activeProductSale?.totalIssuedPrice.toLocaleString()}원`}
                    />
                    <RowData title="작품 매각 가격" content={`${disposalStore.content?.actualSellPrice}원`} />
                    <RowData title="주당 공모가 액" content={`${detailStore.content?.offerPrice.toLocaleString()}원`} />
                    <RowData title="매각 단가" content={`${detailStore.content?.product.sellHistory?.sellPrice}원`} />
                    <RowData
                      title="공모가 대비 가치 상승률"
                      content={{ value: `${detailStore.content?.product.sellHistory?.revenueRate}%`, color: 'error' }}
                    />
                  </ColumnItem>
                </Accordion>
              </AccordionWrap>
            </ProductInfoCard>
            {/* 정산 금액 */}
            <ProductInfoCard>
              <RowData
                title={{ value: '취득가액', size: 18, weight: 600, color: 'text_primary' }}
                content={`${disposalStore.content?.purchaseBalance.toLocaleString()}원`}
              />
              <RowData title="양도가액" content={`${disposalStore.content?.originRevenue.toLocaleString()}원`} />
              <RowData title="기타 소득세" content={`${disposalStore.content?.otherIncomeTax.toLocaleString()}원`} />
              <RowData title="지방 소득세" content={`${disposalStore.content?.localTax.toLocaleString()}원`} />
              <DivideLine />
              <AccordionWrap>
                <RowBetween>
                  <Text size={18} weight={600}>
                    정산 금액
                  </Text>
                  <AmountInfoToggleBox isActive={accordion.amount}>
                    <Text size={18} weight={600}>{`${disposalStore.content?.amount.toLocaleString()}원`}</Text>
                    <ArrowDownIcon onClick={toggleAmountAccordion} />
                  </AmountInfoToggleBox>
                </RowBetween>
                <Accordion isActive={accordion.amount}>
                  <Card $direction="column" $gap={16} $padding="16px" $radius={16} $background="gray_1">
                    <RevenueAmountCardContent>
                      <Text size={14} weight={600} block={true}>
                        나의 양도소득 산출 내역
                      </Text>
                      <Text size={14} lineHeight={22} color="text_secondary">
                        • 양도가액: {disposalStore.content?.originRevenue.toLocaleString()}원<br />• 취득가액:{' '}
                        {disposalStore.content?.purchaseBalance.toLocaleString()}원<br />• 취득 환산가액:{' '}
                        {disposalStore.content?.calculatedBalance.toLocaleString()}원<br />• 인정 필요경비:{' '}
                        {disposalStore.content?.appliedExpense.toLocaleString()}원
                        <br />• 기타 소득 금액: {disposalStore.content?.otherIncome.toLocaleString()}원<br />• 과세대상
                        금액: {otherIncome.toLocaleString()}원
                        <br />• 기타 소득세: {disposalStore.content?.otherIncomeTax.toLocaleString()}원<br />• 지방
                        소득세: {disposalStore.content?.localTax.toLocaleString()}원
                      </Text>
                    </RevenueAmountCardContent>
                    <RevenueAmountCardContent>
                      <Text size={14} weight={600} block={true}>
                        서화, 골동품 양도소득 계산법
                      </Text>
                      <Text size={14} lineHeight={22} color="text_secondary">
                        • 양도가액: 분할 소유권 보유 수량*분할 소유권 매각 단가
                        <br />
                        • 취득가액: 분할 소유권 보유 수량*평균 취득가
                        <br />
                        • 취득 환산가액: 양도가액*인정 필요경비 비율
                        <br />
                        • 인정 필요경비: 취득가액과 취득환산개악 중 큰 금액
                        <br />
                        • 기타소득금액: 양도가액 - 수수료 - 인정 필요경비
                        <br />
                        • 과세대상 금액: 기타소득금액이 5만원 이상일시 해당
                        <br />
                        • 기타 소득세: 기타소득금액*20%(원 단위 이하 금액은 절삭)
                        <br />
                        • 지방 소득세: 기타 소득세*10
                        <br />
                        • 작품 매각 가격 1억 이하: 90%
                        <br />• 작품 매각 가격 1억 초과: (9천만원 + 1억 초과분의 80%)/매각 가격*100
                      </Text>
                    </RevenueAmountCardContent>
                  </Card>
                </Accordion>
              </AccordionWrap>
            </ProductInfoCard>
          </ProductInfoWrap>
        </DetailContent>
      </StyledContent>
      <BottomLabel>
        <Text size={14} weight={700} color="text_white">
          정산 완료 내역
        </Text>
      </BottomLabel>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: 43px;
`

const ProductInfoWrap = styled(ColumnItem)`
  gap: 16px;
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
`

const SaleInfoToggleBox = styled(ColumnItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const AccordionWrap = styled(ColumnItem)``

const Accordion = styled(ColumnItem)<{ isActive: boolean }>`
  overflow: hidden;
  max-height: ${({ isActive }) => (isActive ? '100vh' : 0)};
  transition: 0.5s;
  & > * {
    margin-top: 16px;
  }
`

const AmountInfoToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const RevenueAmountCardContent = styled(ColumnItem)`
  gap: 12px;
`

const BottomLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 33px;
  background-color: ${Color.primary_7};
`
